import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, InputNumber, Table, Button, DatePicker, Input, Checkbox } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import API from '../api';

const { RangePicker } = DatePicker;
const { Option } = Select;

const UpdateCompensationModal = ({ visible, onCancel }) => {
    const [form] = Form.useForm();
    const [products, setProducts] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(false);
    const [productLoading, setProductLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [filters, setFilters] = useState({
        fromDate: null,
        toDate: null,
        storeName: '',
        customerName: '',
        sellerName: '',
        balance: false,
        financialYear: '',
    });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Fetch products based on search query
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setProductLoading(true);
                const response = await API.get('/products/paginate', {
                    params: { search: searchQuery.trim() },
                });
                setProducts(response.data.products || []);
            } catch (error) {
                console.error('Error fetching products:', error);
                Modal.error({ title: 'Error', content: 'Failed to fetch products.' });
            } finally {
                setProductLoading(false);
            }
        };
        fetchProducts();
    }, [searchQuery]);

    // Fetch sales when product is selected or filters/pagination change
    useEffect(() => {
        if (selectedProductId) {
            fetchSales();
        }
    }, [selectedProductId, filters, pagination.current, pagination.pageSize]);

    const fetchSales = async () => {
        setLoading(true);
        try {
            const { fromDate, toDate, storeName, customerName, sellerName, balance, financialYear } = filters;
            const params = {
                productId: selectedProductId,
                page: pagination.current,
                limit: pagination.pageSize,
                ...(fromDate && { fromDate: moment(fromDate).format('YYYY-MM-DD') }),
                ...(toDate && { toDate: moment(toDate).format('YYYY-MM-DD') }),
                ...(storeName && { storeName }),
                ...(customerName && { customerName }),
                ...(sellerName && { sellerName }),
                ...(balance && { balance: 'true' }),
                ...(financialYear && { financialYear }),
            };

            const response = await API.get('/sales/get-invoices-by-id', {
                params,
            });

            setSales(response.data.sales);
            setPagination((prev) => ({
                ...prev,
                total: response.data.pagination.total,
            }));
        } catch (error) {
            console.error('Error fetching sales:', error);
            Modal.error({ title: 'Error', content: error.message });
        } finally {
            setLoading(false);
        }
    };

    // Handle product search
    const handleProductSearch = (value) => {
        setSearchQuery(value);
    };

    // Handle product selection
    const handleProductChange = (value) => {
        setSelectedProductId(value);
        setSelectedRowKeys([]);
        setSales([]);
        setPagination({ current: 1, pageSize: 10, total: 0 });
        setSearchQuery('');
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        setFilters((prev) => ({
            ...prev,
            fromDate: dates ? dates[0] : null,
            toDate: dates ? dates[1] : null,
        }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    // Handle table pagination change
    const handleTableChange = (pagination) => {
        setPagination({
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
        });
    };

    // Handle form submission
    const handleSubmit = async (values) => {
        if (selectedRowKeys.length === 0) {
            Modal.warning({ title: 'Warning', content: 'Please select at least one sale.' });
            return;
        }

        try {
            setLoading(true);
            const { compensatedPrice } = values;

            // Call the new compensation update endpoint
            await API.post('/sales/update-compensation', {
                saleIds: selectedRowKeys,
                productId: selectedProductId,
                compensatedPrice,
            });

            Modal.success({ title: 'Success', content: 'Compensation updated successfully.' });
            form.resetFields();
            setSelectedProductId(null);
            setSelectedRowKeys([]);
            setSales([]);
            setSearchQuery('');
            onCancel();
        } catch (error) {
            console.error('Error updating compensation:', error);
            Modal.error({ title: 'Error', content: error.message });
        } finally {
            setLoading(false);
        }
    };

    // Table columns
    const columns = [
        {
            title: 'Invoice ID',
            dataIndex: 'invoiceId',
            key: 'invoiceId',
        },
        {
            title: 'Store',
            dataIndex: ['storeId', 'name'],
            key: 'storeId',
        },
        {
            title: 'Customer',
            dataIndex: ['customerId', 'name'],
            key: 'customerId',
        },
        {
            title: 'Seller',
            dataIndex: ['sallerId', 'name'],
            key: 'sallerId',
        },
        {
            title: 'Order Date',
            dataIndex: 'orderDate',
            key: 'orderDate',
            render: (date) => moment(date).format('YYYY-MM-DD'),
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
        },
        {
            title: 'Balance Amount',
            dataIndex: 'balanceAmount',
            key: 'balanceAmount',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
        },
        {
            title: 'Products',
            key: 'products',
            render: (_, record) => (
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {record.products.map((product) => (
                        <li
                            key={product.productId._id}
                            style={{
                                fontWeight:
                                    product.productId._id === selectedProductId ? 'bold' : 'normal',
                                color:
                                    product.productId._id === selectedProductId ? '#1890ff' : 'inherit',
                            }}
                        >
                            {product.productId.specifications?.name || 'Unnamed Product'} (Qty: {product.quantity}, Price: {product.salePrice}, Compensation: {product.companyCompensation || 0})
                        </li>
                    ))}
                </ul>
            ),
        },
        {
            title: 'Current Compensation',
            key: 'companyCompensation',
            render: (_, record) => {
                const product = record.products.find((p) => p.productId._id === selectedProductId);
                return product ? product.companyCompensation : 0;
            },
        },
    ];

    // Row selection configuration
    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
        },
    };

    return (
        <Modal
            title="Update Company Compensation"
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={1200}
        >
            <Form
                form={form}
                onFinish={handleSubmit}
                layout="vertical"
                style={{ marginBottom: '20px' }}
            >
                <Form.Item
                    label="Select Product"
                    name="productId"
                    rules={[{ required: true, message: 'Please select a product' }]}
                >
                    <Select
                        showSearch
                        allowClear
                        placeholder="Search and select a product"
                        onSearch={handleProductSearch}
                        onChange={handleProductChange}
                        loading={productLoading}
                        filterOption={false}
                        style={{ width: '100%' }}
                    >
                        {products.map((product) => (
                            <Option key={product._id} value={product._id}>
                                {product.specifications?.name || 'Unnamed Product'}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {selectedProductId && (
                    <>
                        {/* Filters */}
                        <div style={{ marginBottom: '20px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            <RangePicker
                                onChange={handleDateRangeChange}
                                placeholder={['From Date', 'To Date']}
                                style={{ width: '250px' }}
                            />
                            <Input
                                placeholder="Store Name"
                                onChange={(e) => handleFilterChange('storeName', e.target.value)}
                                prefix={<SearchOutlined />}
                                style={{ width: '200px' }}
                            />
                            <Input
                                placeholder="Customer Name"
                                onChange={(e) => handleFilterChange('customerName', e.target.value)}
                                prefix={<SearchOutlined />}
                                style={{ width: '200px' }}
                            />
                            <Input
                                placeholder="Seller Name"
                                onChange={(e) => handleFilterChange('sellerName', e.target.value)}
                                prefix={<SearchOutlined />}
                                style={{ width: '200px' }}
                            />
                            <Input
                                placeholder="Financial Year (e.g., 2024-2025)"
                                onChange={(e) => handleFilterChange('financialYear', e.target.value)}
                                style={{ width: '200px' }}
                            />
                            <Checkbox
                                onChange={(e) => handleFilterChange('balance', e.target.checked)}
                            >
                                Show Only Balances
                            </Checkbox>
                        </div>

                        {/* Sales Table */}
                        <Table
                            rowSelection={rowSelection}
                            columns={columns}
                            dataSource={sales}
                            rowKey="_id"
                            loading={loading}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                            }}
                            onChange={handleTableChange}
                            style={{ marginBottom: '20px' }}
                        />

                        {/* Compensated Price Input */}
                        <Form.Item
                            label="Compensated Price"
                            name="compensatedPrice"
                            rules={[{ required: true, message: 'Please enter the compensated price' }]}
                        >
                            <InputNumber
                                min={0}
                                style={{ width: '200px' }}
                                placeholder="Enter compensated price"
                            />
                        </Form.Item>

                        {/* Submit Button */}
                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Update Compensation
                            </Button>
                        </Form.Item>
                    </>
                )}
            </Form>
        </Modal>
    );
};

export default UpdateCompensationModal;